
import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TextPlugin } from "gsap/TextPlugin";
import backgroundImage from "../../../assets/styles/about_bg.png";

const AboutSection = () => {
  const textRef = useRef(null);
  const sectionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 590 : false);
  const [isTablet, setIsTablet] = useState(() => typeof window !== 'undefined' ? window.innerWidth > 590 && window.innerWidth <= 768 : false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 590);
      setIsTablet(width > 590 && width <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger, TextPlugin);

    const ctx = gsap.context(() => {
      const textElement = textRef.current;

      if (textElement) {
        textElement.innerHTML = "";

        if (isMobile && !isTablet) {
          const mobileLines = [
            "Agastya Agro Limited",
            "Is Driving The Future",
            "Of Indian Agriculture",
            "Through Science,",
            "Sustainability, And A",
            "Farmer-First Mindset",
            "Empowering Soil,",
            "Productivity, And",
            "Farming Communities.",
          ];

          mobileLines.forEach((line, index) => {
            const lineDiv = document.createElement("div");
            lineDiv.className = "about-section__text-line about-section__mobile-text";
            if (index === 0) {
              lineDiv.classList.add("gradient-orange");
            } else {
              lineDiv.classList.add("gradient-gray");
            }
            lineDiv.textContent = line;
            textElement.appendChild(lineDiv);
          });
        } else {
          const desktopLines = [
            "Agastya Agro Limited is driving the future of",
            "Indian agriculture through science, sustainability,",
            "and a farmer-first mindset empowering soil,",
            "productivity, and farming communities.",
          ];

          desktopLines.forEach((line) => {
            const lineDiv = document.createElement("div");
            lineDiv.className = "about-section__text-line about-section__desktop-text";
            lineDiv.textContent = line;
            textElement.appendChild(lineDiv);
          });
        }
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "+=100%",
          pin: true,
          scrub: 1,
        },
      });

      const lines = textRef.current.querySelectorAll(".about-section__text-line");
      lines.forEach((line, index) => {
        tl.to(
          line,
          {
            backgroundSize: "100% 100%",
            webkitTextFillColor: "transparent",
            duration: 0.5,
            ease: "power1.inOut",
          },
          index * 0.3
        );
      });
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, [isMobile, isTablet]);

  return (
    <section
      ref={sectionRef}
      className="container-custom flex items-center justify-center bg-white relative overflow-hidden min-h-screen"
    >
      {/* Background Image */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {/* <div
          className="absolute bg-[#EBEBEB] bg-no-repeat bg-contain 
                     w-[90%] max-w-[1200px] h-auto aspect-[712/509] 
                     top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            WebkitMaskImage: `url(${backgroundImage})`,
            maskImage: `url(${backgroundImage})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskSize: "contain",
            maskSize: "contain",
            WebkitMaskPosition: "center",
            maskPosition: "center",
          }}
        /> */}

        <div
          className="absolute bg-[#EBEBEB] bg-no-repeat bg-contain 
                    w-full max-w-[280px] sm:max-w-[400px] md:max-w-[500px] lg:max-w-[650px] 
                    h-auto aspect-[712/509] 
                    top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            WebkitMaskImage: `url(${backgroundImage})`,
            maskImage: `url(${backgroundImage})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskSize: "contain",
            maskSize: "contain",
            WebkitMaskPosition: "center",
            maskPosition: "center",
          }}
        />
      </div>

      {/* Text */}
      <div className="absolute inset-0 flex items-center justify-center px-[15px] sm:px-[25px] py-[30px] md:py-[60px]">
        <div className="max-w-[1200px] w-full text-center">
          <h1
            ref={textRef}
            className="font-medium text-center font-[LTWave] text-[24px] sm:text-[30px] md:text-[32px] lg:text-[48px] leading-[127%] tracking-[-0.03em]"
          >
            Agastya Agro Limited is driving the future of Indian agriculture through science,
            sustainability, and a farmer-first mindset empowering soil, productivity, and farming
            communities.
          </h1>
        </div>
      </div>

      {/* Scoped CSS */}
      <style>{`
        .about-section__text-line {
          background: linear-gradient(270deg, #FFB603 0%, #DB4B12 41.06%, #593747 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: #6C6C6C;
          background-size: 0% 100%;
          background-repeat: no-repeat;
          background-position: left;
        }
        .about-section__mobile-text {
          text-align: left;
          font-size: 30px;
          line-height: 1.2;
        }

        @media (max-width: 320px) {
          .about-section__mobile-text { font-size: 28px; }
        }

        @media (min-width: 360px) {
          .about-section__mobile-text {
            font-size: 30px;
            line-height: 1.3;
          }
        }
        .about-section__desktop-text {
          text-align: center;
          font-size: 48px;
          line-height: 1.4;
        }

        @media (max-width: 768px) and (min-width: 591px) {
          .about-section__desktop-text {
            font-size: 33px;
            line-height: 1.4;
          }
        }
        .about-section__tablet-text {
          font-size: 33px;
          line-height: 1.4;
          margin-bottom: 10px;
          text-align: center;
        }
      `}</style>
    </section>
  );
};

export default AboutSection;
